import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  imessageService,
} from "../../services/api";
import type {
  IMessageGenerateCodeResponse,
  IMessageHandleResponse,
} from "../../services/api";
import {
  extractErrorMessage,
} from "../../utils/errors";
import {
  OnboardingEvents,
  trackEvent,
} from "../../analytics/posthog";
import {
  QRCodeSVG,
} from "qrcode.react";
import {
  useCountdown,
} from "../../hooks/useCountdown";
import {
  isMobileBrowser,
} from "../../utils/inAppBrowser";
import {
  ChannelModalShell,
  IMESSAGE_ICON,
} from "./ChannelModalShell";

interface IMessageDeepLinkPairingProps {
  isOpen: boolean;
  onClose: () => void;
  onAttemptStarted?: (expiresAt: string) => void;
  onSuccess: () => void;
}

/**
 * The deep-link slice of the generate-code response, with its fields proven
 * present. They are optional on the wire because one response type serves
 * both flows; narrowing once here keeps every render site non-nullable and
 * turns a malformed response into a visible error instead of an `sms:null`
 * link.
 */
interface DeepLinkPairing {
  verification_message: string;
  imessage_link: string;
  from_number: string;
  expires_at: string;
  expires_in_seconds: number;
}

const toDeepLinkPairing = (
  data: IMessageGenerateCodeResponse,
): DeepLinkPairing | null =>
  data.mode === "deeplink"
  && data.verification_message
  && data.imessage_link
  && data.from_number
    ? {
      verification_message: data.verification_message,
      imessage_link: data.imessage_link,
      from_number: data.from_number,
      expires_at: data.expires_at,
      expires_in_seconds: data.expires_in_seconds,
    }
    : null;

/**
 * A comparable fingerprint of the account's linked handles.
 *
 * Success is "this changed", not "the count grew": a connected user adding a
 * second handle gets a deep link that iOS often composes from the handle they
 * already linked, which the backend accepts and records as `last_used_at`
 * without the count moving.
 */
const handleFingerprint = (handles?: IMessageHandleResponse[]): string =>
  (handles ?? [])
    .filter((h) => h.verified)
    .map((h) => `${h.identifier}@${h.last_used_at ?? ""}`)
    .sort()
    .join("|");

const DECLINED_MESSAGE =
  "iMessage isn't available right now. Please try again in a few minutes.";

/**
 * Pair iMessage by sending, not receiving: one button opens Messages (or the
 * Android messaging app) with `VERIFY <code>` composed to the user's assigned
 * number, and the modal completes itself when the handle shows up linked.
 *
 * There is deliberately no way from here to the texted code. Linq's guidance
 * is that the number sending the first message of a thread is what gets
 * flagged, so the outbound send is the thing this flow removes — a desktop
 * without a Messages app gets a QR code and the text to send by hand instead.
 */
export function IMessageDeepLinkPairing({
  isOpen,
  onClose,
  onAttemptStarted,
  onSuccess,
}: IMessageDeepLinkPairingProps) {
  const [pairing, setPairing] = useState<DeepLinkPairing | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const successHandled = useRef(false);
  const statusInFlight = useRef(false);
  // The account's handles as of modal-open — not a truthy `connected` flag,
  // which is already true for a user adding a second handle.
  const baselineFingerprint = useRef<string | null>(null);
  const isOpenRef = useRef(isOpen);
  const onAttemptStartedRef = useRef(onAttemptStarted);
  isOpenRef.current = isOpen;
  onAttemptStartedRef.current = onAttemptStarted;

  // Anchor the server's *remaining* lifetime to this client's clock: a client
  // running ahead of the server would otherwise render "expired" while Redis
  // still holds the code, and since generate-code resumes a live code the
  // retry would hand back the same already-past deadline.
  const expiresAtMs = useMemo(
    () => (pairing ? Date.now() + pairing.expires_in_seconds * 1000 : null),
    [pairing],
  );
  const countdown = useCountdown(
    expiresAtMs === null ? undefined : new Date(expiresAtMs).toISOString(),
  );

  const resetModal = useCallback(() => {
    setPairing(null);
    setError(null);
    baselineFingerprint.current = null;
    countdown.reset();
  }, [countdown.reset]);

  const handleSuccess = useCallback(() => {
    if (successHandled.current) return;
    successHandled.current = true;
    trackEvent(OnboardingEvents.IMESSAGE_CONNECTED);
    resetModal();
    onClose();
    onSuccess();
  }, [onClose, onSuccess, resetModal]);

  /** Complete when the account's handles change, seeding the baseline first. */
  const completeIfHandlesChanged = useCallback(async (): Promise<boolean> => {
    if (statusInFlight.current) return false;
    statusInFlight.current = true;
    try {
      const status = await imessageService.getStatus();
      const fingerprint = handleFingerprint(status.handles);
      if (baselineFingerprint.current === null) {
        baselineFingerprint.current = fingerprint;
        return false;
      }
      if (fingerprint !== baselineFingerprint.current) {
        if (isOpenRef.current) handleSuccess();
        return true;
      }
    } catch {
      // A status failure must not block generating or using a link.
    } finally {
      statusInFlight.current = false;
    }
    return false;
  }, [handleSuccess]);

  const generateLink = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The status read seeds the baseline; nothing can link a handle before
      // the user has seen the link, so it need not precede the generate call.
      const baseline = completeIfHandlesChanged();
      try {
        const [changed, data] = await Promise.all([
          baseline,
          imessageService.generateVerificationCode(),
        ]);
        if (changed || !isOpenRef.current) return;
        if (data.mode !== "deeplink") {
          // The server declined to hand out a link — the assigned number is
          // flagged and would not deliver the text in time (GET-13757). Say
          // so and let the user retry: the evacuation moves them to a healthy
          // number within the half hour, and texting from a flagged number
          // is blocked anyway, so there is no code path to fall back to.
          trackEvent(OnboardingEvents.IMESSAGE_CONNECTION_FAILED, {
            error: data.declined_reason ?? "declined",
          });
          setError(DECLINED_MESSAGE);
          return;
        }
        const next = toDeepLinkPairing(data);
        if (!next) {
          trackEvent(OnboardingEvents.IMESSAGE_CONNECTION_FAILED, {
            error: "missing_deeplink_fields",
          });
          setError("We couldn't set this up just now. Nothing has changed on your account.");
          return;
        }
        setPairing(next);
        onAttemptStartedRef.current?.(next.expires_at);
        trackEvent(OnboardingEvents.IMESSAGE_CODE_GENERATED);
      } catch (err: unknown) {
        if (!isOpenRef.current) return;
        // Pairing can complete between the status check and generation.
        await baseline;
        if (await completeIfHandlesChanged()) return;
        const message = extractErrorMessage(
          err,
          "We couldn't set this up just now. Nothing has changed on your account.",
        );
        trackEvent(OnboardingEvents.IMESSAGE_CONNECTION_FAILED, { error: message });
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [completeIfHandlesChanged]);

  useEffect(() => {
    // `imessage_modal_opened` is fired once by IMessagePairingModal, not here:
    // switching flows must not count a second open.
    if (isOpen) successHandled.current = false;
  }, [isOpen]);

  useEffect(() => {
    // A generation error (or a declined link) only retries when the user
    // clicks "Try again".
    if (isOpen && !pairing && !loading && !error) {
      void generateLink();
    }
  }, [error, generateLink, isOpen, loading, pairing]);

  const handleRegenerate = () => {
    trackEvent(OnboardingEvents.IMESSAGE_CODE_REGENERATE_CLICKED);
    // Clearing the state is the whole action: the effect above re-fires.
    setPairing(null);
    setError(null);
  };

  // Desktops may have no Messages app (Windows, Linux) or one not signed in
  // (a work Mac): the phone scans this and lands in Messages with the text
  // composed, so on a desktop the QR leads and the link is the alternative.
  // Memoised so the once-a-second countdown re-render doesn't re-encode it.
  const showQr = !isMobileBrowser();
  const link = pairing?.imessage_link;
  const qrCode = useMemo(
    () => (showQr && link ? <QRCodeSVG value={link} size={132} /> : null),
    [link, showQr],
  );

  const handleClose = () => {
    trackEvent(OnboardingEvents.IMESSAGE_MODAL_CLOSED);
    resetModal();
    onClose();
  };

  useEffect(() => {
    if (!isOpen || !pairing || expiresAtMs === null) return;

    // Not gated on document.hidden: a visible page can report hidden (an
    // unfocused window on a second monitor), and this modal promises to
    // detect the connection on its own.
    const tick = async () => {
      if (expiresAtMs <= Date.now()) {
        clearInterval(interval);
        return;
      }
      await completeIfHandlesChanged();
    };
    let foregroundCheck: ReturnType<typeof setTimeout> | undefined;
    const onForeground = () => {
      if (foregroundCheck) clearTimeout(foregroundCheck);
      // Ignores the deadline on purpose: the user is sent out to Messages
      // and may come back after the code expired but after it worked.
      foregroundCheck = setTimeout(() => void completeIfHandlesChanged(), 100);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onForeground();
    };

    const interval: ReturnType<typeof setInterval> = setInterval(
      () => void tick(),
      5000,
    );
    window.addEventListener("focus", onForeground);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      if (foregroundCheck) clearTimeout(foregroundCheck);
      window.removeEventListener("focus", onForeground);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [completeIfHandlesChanged, expiresAtMs, isOpen, pairing]);

  return (
    <ChannelModalShell
      isOpen={isOpen}
      dialogOnClose={() => {}}
      onClose={handleClose}
      iconWrapperClass="bg-gray-100"
      icon={IMESSAGE_ICON}
      title="Connect iMessage"
      loading={loading}
      loadingLabel="Getting things ready..."
      spinnerColorClass="border-blue-600"
      error={error}
      errorAction={
        <button
          onClick={handleRegenerate}
          className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
        >
          Try again
        </button>
      }
    >
      {pairing && !loading && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-4">
            {qrCode
              ? "Scan with your phone and send the message that opens. We'll connect as soon as it arrives."
              : "Send the message that opens. We'll connect as soon as it arrives."}
          </p>

          {qrCode && (
            <div className="mb-4 flex justify-center">
              <div className="bg-white p-3 rounded-md border border-gray-200">
                {qrCode}
              </div>
            </div>
          )}

          <a
            href={pairing.imessage_link}
            onClick={() => trackEvent(OnboardingEvents.IMESSAGE_LINK_CLICKED)}
            className={
              qrCode
                ? "block w-full border border-blue-600 text-blue-600 px-4 py-2 rounded-md text-center text-sm font-medium hover:bg-blue-50 transition-colors"
                : "block w-full bg-blue-600 text-white px-4 py-2 rounded-md text-center text-sm font-medium hover:bg-blue-700 transition-colors"
            }
          >
            {qrCode ? "Open Messages on this computer" : "Open Messages"}
          </a>

          <p className="mt-3 text-center text-xs text-gray-500">
            Or text <span className="font-mono">{pairing.verification_message}</span> to {pairing.from_number}.
          </p>

          {countdown.timeLeft > 0 ? (
            <p className="mt-4 text-center text-xs text-gray-500">
              Expires in <strong>{countdown.formatted}</strong>
            </p>
          ) : (
            <p className="mt-4 text-center text-xs text-red-600">
              That link timed out.{" "}
              <button
                onClick={handleRegenerate}
                className="text-red-600 hover:text-red-500 underline font-medium"
              >
                Get a new one
              </button>
            </p>
          )}
        </div>
      )}
    </ChannelModalShell>
  );
}
